"use client";

import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { CopyButton } from "@/app/components/ui/copy-button";
import { 
  Shuffle, 
  Copy, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Dice1,
  Dice2,
  Dice3,
  Dice4,
  Dice5,
  Dice6,
  Hash,
  Calculator,
  Target,
  Zap,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

interface GeneratedNumber {
  id: string;
  value: number;
  timestamp: Date;
  type: string;
  settings: {
    min: number;
    max: number;
    count: number;
    allowDuplicates: boolean;
  };
}

interface NumberHistory {
  id: string;
  numbers: number[];
  timestamp: Date;
  type: string;
  settings: any;
}

type GeneratorType = "integer" | "decimal" | "dice" | "lottery" | "custom";

export default function RandomNumberGeneratorPage() {
  const [generatedNumbers, setGeneratedNumbers] = useState<GeneratedNumber[]>([]);
  const [history, setHistory] = useState<NumberHistory[]>([]);
  const [activeTab, setActiveTab] = useState<GeneratorType>("integer");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Integer settings
  const [minValue, setMinValue] = useState(1);
  const [maxValue, setMaxValue] = useState(100);
  const [count, setCount] = useState(1);
  const [allowDuplicates, setAllowDuplicates] = useState(true);

  // Decimal settings
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [decimalMin, setDecimalMin] = useState(0);
  const [decimalMax, setDecimalMax] = useState(1);

  // Dice settings
  const [diceCount, setDiceCount] = useState(1);
  const [diceSides, setDiceSides] = useState(6);

  // Lottery settings
  const [lotteryNumbers, setLotteryNumbers] = useState(6);
  const [lotteryMax, setLotteryMax] = useState(49);

  // Custom settings
  const [customList, setCustomList] = useState("");
  const [customCount, setCustomCount] = useState(1);

  // TRPC mutations
  const generateIntegerMutation = trpc.randomNumber.generateInteger.useMutation();
  const generateDecimalMutation = trpc.randomNumber.generateDecimal.useMutation();
  const generateDiceMutation = trpc.randomNumber.generateDice.useMutation();
  const generateLotteryMutation = trpc.randomNumber.generateLottery.useMutation();
  const generateCustomMutation = trpc.randomNumber.generateCustom.useMutation();
  const analyzeMutation = trpc.randomNumber.analyzeNumbers.useMutation();
  const presetsQuery = trpc.randomNumber.getPresets.useQuery();

  // Generate numbers using TRPC
  const generateIntegerNumbers = useCallback(async () => {
    try {
      const result = await generateIntegerMutation.mutateAsync({
        min: minValue,
        max: maxValue,
        count,
        allowDuplicates,
      });

      if (result.success) {
        return result.numbers;
      }
      return [];
    } catch (error) {
      toast.error("Failed to generate integer numbers");
      return [];
    }
  }, [minValue, maxValue, count, allowDuplicates, generateIntegerMutation]);

  const generateDecimalNumbers = useCallback(async () => {
    try {
      const result = await generateDecimalMutation.mutateAsync({
        min: decimalMin,
        max: decimalMax,
        count,
        decimalPlaces,
      });

      if (result.success) {
        return result.numbers;
      }
      return [];
    } catch (error) {
      toast.error("Failed to generate decimal numbers");
      return [];
    }
  }, [decimalMin, decimalMax, count, decimalPlaces, generateDecimalMutation]);

  const generateDiceRolls = useCallback(async () => {
    try {
      const result = await generateDiceMutation.mutateAsync({
        diceCount,
        diceSides,
      });

      if (result.success) {
        return result.numbers;
      }
      return [];
    } catch (error) {
      toast.error("Failed to generate dice rolls");
      return [];
    }
  }, [diceCount, diceSides, generateDiceMutation]);

  const generateLotteryNumbers = useCallback(async () => {
    try {
      const result = await generateLotteryMutation.mutateAsync({
        numberCount: lotteryNumbers,
        maxNumber: lotteryMax,
      });

      if (result.success) {
        return result.numbers;
      }
      return [];
    } catch (error) {
      toast.error("Failed to generate lottery numbers");
      return [];
    }
  }, [lotteryNumbers, lotteryMax, generateLotteryMutation]);

  const generateCustomNumbers = useCallback(async () => {
    const items = customList.split(',').map(item => item.trim()).filter(item => item);
    
    if (items.length === 0) {
      toast.error("Please enter some items separated by commas");
      return [];
    }

    try {
      const result = await generateCustomMutation.mutateAsync({
        items,
        count: customCount,
      });

      if (result.success) {
        return result.items;
      }
      return [];
    } catch (error) {
      toast.error("Failed to generate custom selection");
      return [];
    }
  }, [customList, customCount, generateCustomMutation]);

  const generateNumbers = useCallback(async () => {
    let numbers: (number | string)[] = [];
    let type = "";

    try {
      switch (activeTab) {
        case "integer":
          numbers = await generateIntegerNumbers();
          type = `Integer (${minValue}-${maxValue})`;
          break;
        case "decimal":
          numbers = await generateDecimalNumbers();
          type = `Decimal (${decimalMin}-${decimalMax}, ${decimalPlaces} places)`;
          break;
        case "dice":
          numbers = await generateDiceRolls();
          type = `${diceCount} × ${diceSides}-sided dice`;
          break;
        case "lottery":
          numbers = await generateLotteryNumbers();
          type = `Lottery (${lotteryNumbers} from ${lotteryMax})`;
          break;
        case "custom":
          numbers = await generateCustomNumbers();
          type = `Custom list (${customCount} items)`;
          break;
      }

      if (numbers.length === 0) return;

      // Add to generated numbers
      const newGeneratedNumbers = numbers.map((value, index) => ({
        id: `${Date.now()}-${index}`,
        value: typeof value === 'string' ? parseFloat(value) || 0 : value,
        timestamp: new Date(),
        type,
        settings: {
          min: minValue,
          max: maxValue,
          count,
          allowDuplicates
        }
      }));

      setGeneratedNumbers(prev => [...newGeneratedNumbers, ...prev]);

      // Add to history
      const historyEntry: NumberHistory = {
        id: Date.now().toString(),
        numbers: numbers.map(n => typeof n === 'string' ? parseFloat(n) || 0 : n),
        timestamp: new Date(),
        type,
        settings: {
          min: minValue,
          max: maxValue,
          count,
          allowDuplicates,
          activeTab
        }
      };

      setHistory(prev => [historyEntry, ...prev.slice(0, 49)]); // Keep last 50 entries

      toast.success(`Generated ${numbers.length} ${activeTab} number${numbers.length > 1 ? 's' : ''}!`);
    } catch (error) {
      toast.error("Failed to generate numbers");
    }
  }, [activeTab, generateIntegerNumbers, generateDecimalNumbers, generateDiceRolls, generateLotteryNumbers, generateCustomNumbers, minValue, maxValue, count, allowDuplicates, decimalMin, decimalMax, decimalPlaces, diceCount, diceSides, lotteryNumbers, lotteryMax, customList, customCount]);

  const copyToClipboard = useCallback(async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setGeneratedNumbers([]);
    toast.success("History cleared!");
  }, []);

  const exportNumbers = useCallback(() => {
    const data = {
      generated: generatedNumbers,
      history: history,
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `random-numbers-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Numbers exported!");
  }, [generatedNumbers, history]);

  const getDiceIcon = (value: number) => {
    const icons = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];
    const IconComponent = icons[value - 1] || Dice1;
    return <IconComponent className="h-4 w-4" />;
  };

  const getTotalSum = useMemo(() => {
    return generatedNumbers.reduce((sum, item) => sum + item.value, 0);
  }, [generatedNumbers]);

  const getAverage = useMemo(() => {
    if (generatedNumbers.length === 0) return 0;
    return getTotalSum / generatedNumbers.length;
  }, [generatedNumbers, getTotalSum]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Random Number Generator</h1>
        <p className="text-muted-foreground">
          Generate random numbers for various purposes including integers, decimals, dice rolls, lottery numbers, and custom selections.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shuffle className="h-5 w-5" />
                Number Generator
              </CardTitle>
              <CardDescription>
                Choose your generator type and configure the settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as GeneratorType)}>
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="integer">Integer</TabsTrigger>
                  <TabsTrigger value="decimal">Decimal</TabsTrigger>
                  <TabsTrigger value="dice">Dice</TabsTrigger>
                  <TabsTrigger value="lottery">Lottery</TabsTrigger>
                  <TabsTrigger value="custom">Custom</TabsTrigger>
                </TabsList>

                <TabsContent value="integer" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="minValue">Minimum Value</Label>
                      <Input
                        id="minValue"
                        type="number"
                        value={minValue}
                        onChange={(e) => setMinValue(parseInt(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maxValue">Maximum Value</Label>
                      <Input
                        id="maxValue"
                        type="number"
                        value={maxValue}
                        onChange={(e) => setMaxValue(parseInt(e.target.value) || 100)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="allowDuplicates"
                      checked={allowDuplicates}
                      onCheckedChange={(checked) => setAllowDuplicates(checked as boolean)}
                    />
                    <Label htmlFor="allowDuplicates">Allow duplicate numbers</Label>
                  </div>
                </TabsContent>

                <TabsContent value="decimal" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="decimalMin">Minimum Value</Label>
                      <Input
                        id="decimalMin"
                        type="number"
                        step="0.01"
                        value={decimalMin}
                        onChange={(e) => setDecimalMin(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="decimalMax">Maximum Value</Label>
                      <Input
                        id="decimalMax"
                        type="number"
                        step="0.01"
                        value={decimalMax}
                        onChange={(e) => setDecimalMax(parseFloat(e.target.value) || 1)}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Decimal Places: {decimalPlaces}</Label>
                    <Slider
                      value={[decimalPlaces]}
                      onValueChange={(value) => setDecimalPlaces(value[0])}
                      max={10}
                      min={0}
                      step={1}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {count}</Label>
                    <Slider
                      value={[count]}
                      onValueChange={(value) => setCount(value[0])}
                      max={100}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="dice" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="diceCount">Number of Dice</Label>
                      <Input
                        id="diceCount"
                        type="number"
                        value={diceCount}
                        onChange={(e) => setDiceCount(parseInt(e.target.value) || 1)}
                        min={1}
                        max={10}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="diceSides">Sides per Die</Label>
                      <Select value={diceSides.toString()} onValueChange={(value) => setDiceSides(parseInt(value))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4">4-sided (D4)</SelectItem>
                          <SelectItem value="6">6-sided (D6)</SelectItem>
                          <SelectItem value="8">8-sided (D8)</SelectItem>
                          <SelectItem value="10">10-sided (D10)</SelectItem>
                          <SelectItem value="12">12-sided (D12)</SelectItem>
                          <SelectItem value="20">20-sided (D20)</SelectItem>
                          <SelectItem value="100">100-sided (D100)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="lottery" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="lotteryNumbers">Number of Numbers</Label>
                      <Input
                        id="lotteryNumbers"
                        type="number"
                        value={lotteryNumbers}
                        onChange={(e) => setLotteryNumbers(parseInt(e.target.value) || 6)}
                        min={1}
                        max={20}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lotteryMax">Maximum Number</Label>
                      <Input
                        id="lotteryMax"
                        type="number"
                        value={lotteryMax}
                        onChange={(e) => setLotteryMax(parseInt(e.target.value) || 49)}
                        min={1}
                        max={1000}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="custom" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customList">Custom Items (comma-separated)</Label>
                    <Input
                      id="customList"
                      placeholder="apple, banana, cherry, date, elderberry"
                      value={customList}
                      onChange={(e) => setCustomList(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Count: {customCount}</Label>
                    <Slider
                      value={[customCount]}
                      onValueChange={(value) => setCustomCount(value[0])}
                      max={20}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              {/* Presets */}
              {presetsQuery.data?.success && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Quick Presets</Label>
                  <div className="flex gap-2 flex-wrap">
                    {presetsQuery.data.presets[activeTab]?.map((preset: any, index: number) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (activeTab === "integer") {
                            setMinValue(preset.min);
                            setMaxValue(preset.max);
                            setCount(preset.count);
                          } else if (activeTab === "decimal") {
                            setDecimalMin(preset.min);
                            setDecimalMax(preset.max);
                            setCount(preset.count);
                            setDecimalPlaces(preset.decimalPlaces);
                          } else if (activeTab === "dice") {
                            setDiceCount(preset.diceCount);
                            setDiceSides(preset.diceSides);
                          } else if (activeTab === "lottery") {
                            setLotteryNumbers(preset.numberCount);
                            setLotteryMax(preset.maxNumber);
                          } else if (activeTab === "custom") {
                            setCustomList(preset.items.join(", "));
                            setCustomCount(preset.count);
                          }
                          toast.success("Preset loaded!");
                        }}
                        className="hover-lift"
                        disabled={presetsQuery.isLoading}
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  onClick={generateNumbers} 
                  className="flex-1"
                  disabled={
                    generateIntegerMutation.isPending ||
                    generateDecimalMutation.isPending ||
                    generateDiceMutation.isPending ||
                    generateLotteryMutation.isPending ||
                    generateCustomMutation.isPending
                  }
                >
                  {(generateIntegerMutation.isPending ||
                    generateDecimalMutation.isPending ||
                    generateDiceMutation.isPending ||
                    generateLotteryMutation.isPending ||
                    generateCustomMutation.isPending) ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Shuffle className="h-4 w-4 mr-2" />
                      Generate Numbers
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearHistory}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" onClick={exportNumbers}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Generated Numbers Display */}
          {generatedNumbers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Hash className="h-5 w-5" />
                  Generated Numbers
                </CardTitle>
                <CardDescription>
                  {generatedNumbers.length} number{generatedNumbers.length > 1 ? 's' : ''} generated
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
                  {generatedNumbers.slice(0, 50).map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 border rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-2">
                        {activeTab === "dice" && getDiceIcon(item.value)}
                        <span className="font-mono text-sm">{item.value}</span>
                      </div>
                      <CopyButton
                        text={item.value.toString()}
                        onCopy={() => copyToClipboard(item.value.toString(), index)}
                        className="h-6 w-6"
                      />
                    </div>
                  ))}
                </div>
                {generatedNumbers.length > 50 && (
                  <p className="text-sm text-muted-foreground">
                    Showing first 50 numbers. Total: {generatedNumbers.length}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Statistics and History */}
        <div className="space-y-6">
          {/* Statistics */}
          {generatedNumbers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{generatedNumbers.length}</div>
                    <div className="text-sm text-muted-foreground">Total Numbers</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getTotalSum}</div>
                    <div className="text-sm text-muted-foreground">Sum</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverage.toFixed(2)}</div>
                    <div className="text-sm text-muted-foreground">Average</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">
                      {Math.min(...generatedNumbers.map(n => n.value))}
                    </div>
                    <div className="text-sm text-muted-foreground">Minimum</div>
                  </div>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <div className="text-2xl font-bold">
                    {Math.max(...generatedNumbers.map(n => n.value))}
                  </div>
                  <div className="text-sm text-muted-foreground">Maximum</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} generation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {entry.numbers.slice(0, 8).map((num, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-background border rounded text-xs font-mono"
                          >
                            {num}
                          </span>
                        ))}
                        {entry.numbers.length > 8 && (
                          <span className="px-2 py-1 bg-background border rounded text-xs">
                            +{entry.numbers.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use dice rolls for games and probability simulations</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Lottery mode automatically sorts numbers and prevents duplicates</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Custom mode is perfect for random selections from lists</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Export your results for analysis or record keeping</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
